import React, { useState } from "react";
import { useAtom } from "jotai";
import {
  themeAtom,
  labelsAtom,
  labelColoursAtom,
  isAdminAtom,
} from "../../../../-Atoms";
import { db, doc, updateDoc } from "../../../../firebaseConfig";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Collapse,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  DragIndicator as DragIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

import { v4 as uuidv4 } from "uuid";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const LabelMenuV2 = ({ jobData, open, setOpen }) => {
  const [theme] = useAtom(themeAtom);
  const [labels, setLabels] = useAtom(labelsAtom);
  const [labelColours] = useAtom(labelColoursAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [searchTerm, setSearchTerm] = useState("");
  const [editingLabel, setEditingLabel] = useState(null);
  const [newLabelName, setNewLabelName] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [showCreateLabel, setShowCreateLabel] = useState(false);

  const colorOptions = [
    "green_light", "yellow_light", "orange_light", "red_light", "purple_light",
    "blue_light", "sky_light", "lime_light", "pink_light", "black_light",
    "green", "yellow", "orange", "red", "purple",
    "blue", "sky", "lime", "pink", "black",
    "green_dark", "yellow_dark", "orange_dark", "red_dark", "purple_dark",
    "blue_dark", "sky_dark", "lime_dark", "pink_dark", "black_dark",
  ];

  // Filter labels based on search
  const filteredLabels = labels.filter(label =>
    label.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClose = () => {
    setOpen(false);
    setEditingLabel(null);
    setNewLabelName("");
    setSelectedColor("");
    setSearchTerm("");
    setShowCreateLabel(false);
  };

  const toggleLabel = (labelId) => {
    if (jobData?.idLabelsPermanent?.includes(labelId)) return;
    
    const newIdLabels = jobData?.idLabels?.includes(labelId)
      ? jobData.idLabels.filter((id) => id !== labelId)
      : [...(jobData?.idLabels || []), labelId];
    
    updateDoc(doc(db, "JobData", jobData.id), {
      idLabels: newIdLabels,
    });
  };

  const createNewLabel = () => {
    if (!newLabelName.trim()) return;
    
    const newLabel = {
      color: selectedColor || "",
      id: uuidv4(),
      name: newLabelName.trim(),
      kanbanHidden: false,
      locked: false,
    };
    
    const updatedLabels = [...labels, newLabel];
    setLabels(updatedLabels);
    updateDoc(doc(db, "AdminCollection", "labels"), {
      labels: updatedLabels,
    });
    
    setNewLabelName("");
    setSelectedColor("");
    setShowCreateLabel(false);
  };

  const updateLabel = (updatedLabel) => {
    const updatedLabels = labels.map((l) =>
      l.id === updatedLabel.id ? updatedLabel : l
    );
    setLabels(updatedLabels);
    updateDoc(doc(db, "AdminCollection", "labels"), {
      labels: updatedLabels,
    });
    setEditingLabel(null);
  };

  const deleteLabel = (labelId) => {
    const updatedLabels = labels.filter((l) => l.id !== labelId);
    setLabels(updatedLabels);
    updateDoc(doc(db, "AdminCollection", "labels"), {
      labels: updatedLabels,
    });
    setEditingLabel(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination || !isAdmin) return;
    if (result.destination.index === result.source.index) return;

    const reorderedLabels = Array.from(labels);
    const [movedLabel] = reorderedLabels.splice(result.source.index, 1);
    reorderedLabels.splice(result.destination.index, 0, movedLabel);

    setLabels(reorderedLabels);
    updateDoc(doc(db, "AdminCollection", "labels"), {
      labels: reorderedLabels,
    });
  };

  const ColorPicker = ({ selectedColor, onColorSelect, disabled = false }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 1,
        p: 2,
        border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
        borderRadius: 2,
        backgroundColor: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {colorOptions.map((colorKey) => (
        <Box
          key={colorKey}
          onClick={() => onColorSelect(colorKey)}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            backgroundColor: labelColours[colorKey]?.[theme] || "#ccc",
            cursor: "pointer",
            border: selectedColor === colorKey 
              ? `3px solid ${theme === "dark" ? "#90caf9" : "#1976d2"}`
              : `1px solid ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: theme === "dark" 
                ? "0 4px 12px rgba(0,0,0,0.4)" 
                : "0 4px 12px rgba(0,0,0,0.2)",
            },
          }}
        />
      ))}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: "80vh",
          maxHeight: "80vh",
          background: theme === "dark" 
            ? "linear-gradient(135deg, rgba(28, 28, 28, 0.95) 0%, rgba(35, 35, 35, 0.95) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          border: theme === "dark" 
            ? "1px solid rgba(255, 255, 255, 0.1)" 
            : "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: theme === "dark"
            ? "0 24px 48px rgba(0,0,0,0.4)"
            : "0 24px 48px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Manage Labels
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, userSelect: "none", flex: 1, overflow: "auto" }}>
        {/* Search Bar */}
        <Box sx={{ p: 3, pb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search labels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: theme === "dark" 
                  ? "rgba(255,255,255,0.05)" 
                  : "rgba(0,0,0,0.03)",
              },
            }}
          />
        </Box>

        {/* New Label Creation */}
        {isAdmin && (
          <Box sx={{ px: 3, pb: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowCreateLabel(!showCreateLabel)}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.8rem",
                py: 1,
                justifyContent: "flex-start",
                borderColor: theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                "&:hover": {
                  borderColor: theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                  backgroundColor: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                },
              }}
            >
              Create New Label
            </Button>
            
            <Collapse in={showCreateLabel}>
              <Card
                sx={{
                  mt: 1.5,
                  backgroundColor: theme === "dark" 
                    ? "rgba(255,255,255,0.03)" 
                    : "rgba(0,0,0,0.02)",
                  border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Label name..."
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  
                  <Typography variant="caption" sx={{ mb: 1, display: "block", opacity: 0.7 }}>
                    Choose Color (optional)
                  </Typography>
                  
                  <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center" }}>
                    <ColorPicker
                      selectedColor={selectedColor}
                      onColorSelect={setSelectedColor}
                    />
                  </Box>
                  
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={createNewLabel}
                      disabled={!newLabelName.trim()}
                      sx={{ 
                        flex: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        py: 0.75,
                      }}
                    >
                      Create
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowCreateLabel(false);
                        setNewLabelName("");
                        setSelectedColor("");
                      }}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        py: 0.75,
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Collapse>
          </Box>
        )}

        {/* Labels List */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Available Labels ({filteredLabels.length})
          </Typography>
          
          {filteredLabels.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: "center", py: 4, opacity: 0.7 }}>
              {searchTerm ? "No labels match your search" : "No labels created yet"}
            </Typography>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="labelsList">
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {filteredLabels.map((label, index) => (
                      <Draggable
                        key={label.id}
                        draggableId={label.id}
                        index={index}
                        isDragDisabled={!isAdmin || label.locked}
                      >
                        {(provided, snapshot) => {
                          // Fix for dragging styles
                          if (snapshot.isDragging) {
                            provided.draggableProps.style = {
                              ...provided.draggableProps.style,
                              left: provided.draggableProps.style?.offsetLeft,
                              top: provided.draggableProps.style?.offsetTop,
                            };
                          }
                          
                          return (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              onClick={() => toggleLabel(label.id)}
                              sx={{
                                mb: 1,
                                userSelect: "none",
                                backgroundColor: snapshot.isDragging
                                  ? (theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)")
                                  : jobData?.idLabels?.includes(label.id)
                                    ? (theme === "dark" ? "rgba(144, 202, 249, 0.08)" : "rgba(25, 118, 210, 0.06)")
                                    : (theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"),
                                border: `1px solid ${
                                  jobData?.idLabels?.includes(label.id)
                                    ? (theme === "dark" ? "rgba(144, 202, 249, 0.2)" : "rgba(25, 118, 210, 0.15)")
                                    : (theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)")
                                }`,
                                boxShadow: snapshot.isDragging 
                                  ? "0 8px 24px rgba(0,0,0,0.2)" 
                                  : "none",
                                cursor: jobData?.idLabelsPermanent?.includes(label.id) ? "default" : "pointer",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": jobData?.idLabelsPermanent?.includes(label.id) ? {} : {
                                  backgroundColor: jobData?.idLabels?.includes(label.id)
                                    ? (theme === "dark" ? "rgba(144, 202, 249, 0.12)" : "rgba(25, 118, 210, 0.08)")
                                    : (theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"),
                                  transform: "translateY(-1px)",
                                },
                              }}
                            >
                            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, userSelect: "none" }}>
                                {/* Drag Handle */}
                                {isAdmin && !label.locked && (
                                  <Box {...provided.dragHandleProps}>
                                    <DragIcon sx={{ opacity: 0.5, cursor: "grab" }} />
                                  </Box>
                                )}
                                {/* Spacer for locked labels to maintain alignment */}
                                {(!isAdmin || label.locked) && (
                                  <Box sx={{ width: 24, height: 24 }} />
                                )}

                                {/* Checkbox */}
                                <Checkbox
                                  size="small"
                                  checked={jobData?.idLabels?.includes(label.id) || false}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => toggleLabel(label.id)}
                                  disabled={jobData?.idLabelsPermanent?.includes(label.id)}
                                  sx={{
                                    "&.Mui-checked": {
                                      color: theme === "dark" ? "#90caf9" : "#1976d2",
                                    },
                                  }}
                                />

                                {/* Label Display */}
                                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.75, userSelect: "none" }}>
                                  <Chip
                                    label={label.name}
                                    size="small"
                                    sx={{
                                      backgroundColor: label.color
                                        ? labelColours[label.color]?.[theme]
                                        : (theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                                      color: "white",
                                      fontWeight: 600,
                                      borderRadius: 1.5,
                                      height: 28,
                                      fontSize: "0.875rem",
                                      "& .MuiChip-label": {
                                        px: 1.5,
                                        py: 0,
                                        fontSize: "0.875rem",
                                      },
                                    }}
                                  />
                                  {label.locked && <LockIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
                                  {label.kanbanHidden && <VisibilityOffIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
                                </Box>

                                {/* Edit Button */}
                                {isAdmin && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingLabel(label);
                                    }}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      backgroundColor: theme === "dark" 
                                        ? "rgba(255,255,255,0.05)" 
                                        : "rgba(0,0,0,0.05)",
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Box>
      </DialogContent>

      {/* Edit Label Dialog */}
      <Dialog
        open={!!editingLabel}
        onClose={() => setEditingLabel(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme === "dark" 
              ? "rgba(28, 28, 28, 0.98)" 
              : "rgba(255, 255, 255, 0.98)",
          },
        }}
      >
        {editingLabel && (
          <>
            <DialogTitle>
              {editingLabel.locked ? "View Label" : "Edit Label"}
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Label Name"
                value={editingLabel.name}
                onChange={(e) => setEditingLabel({ ...editingLabel, name: e.target.value })}
                disabled={editingLabel.locked}
                sx={{ mb: 2, mt: 1 }}
              />
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Color
              </Typography>
              <ColorPicker
                selectedColor={editingLabel.color}
                onColorSelect={(color) => setEditingLabel({ ...editingLabel, color })}
                disabled={editingLabel.locked}
              />

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingLabel.kanbanHidden || false}
                      onChange={(e) => setEditingLabel({ ...editingLabel, kanbanHidden: e.target.checked })}
                      disabled={editingLabel.locked}
                    />
                  }
                  label="Hide on Kanban Board"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingLabel.locked || false}
                      onChange={(e) => setEditingLabel({ ...editingLabel, locked: e.target.checked })}
                      icon={<LockOpenIcon />}
                      checkedIcon={<LockIcon />}
                    />
                  }
                  label="Lock Label"
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => updateLabel(editingLabel)}
                  sx={{ flex: 1, borderRadius: 2, textTransform: "none" }}
                >
                  {editingLabel.locked ? "Close" : "Save"}
                </Button>
                {!editingLabel.locked && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => deleteLabel(editingLabel.id)}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Dialog>
  );
};

export default LabelMenuV2;